import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API_BASE } from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatApiError, formatDateTime } from "../lib/format";
import { Card, Loading } from "../components/UI";
import { ShieldAlert, Image as ImageIcon, Video, X, Send, Trash2, MessageCircle, Tag as TagIcon, AlertTriangle } from "lucide-react";

const CATEGORIES = [
  { v: "general", label: "General awareness" },
  { v: "suspicious_activity", label: "Suspicious activity" },
  { v: "lost_found", label: "Lost & found" },
  { v: "utilities", label: "Utilities (water / power)" },
  { v: "safety", label: "Safety concern" },
  { v: "other", label: "Other" },
];

const CAT_COLOR = {
  suspicious_activity: "bg-[#FEF0F0] text-[#C92A2A]",
  safety: "bg-[#FEF0F0] text-[#C92A2A]",
  utilities: "bg-[#FDF6E3] text-[#7A6810]",
  lost_found: "bg-[#E8F5EE] text-[#0E5C3A]",
  general: "bg-[#E8F5EE] text-[#0E5C3A]",
  other: "bg-[#F0F2F4] text-[#5E7166]",
};

const MAX_PHOTOS = 3;
const MAX_VIDEO_SEC = 20;
const MAX_VIDEO_BYTES = 60 * 1024 * 1024;  // 60 MB — room for a 20s 1080p clip
const COMPRESS_MAX_HEIGHT = 720;            // downscale 1080p → 720p
const COMPRESS_BITRATE = 1_200_000;         // 1.2 Mbps target

// Build a viewable URL for a storage path. Backend `/api/files/view` requires
// auth — the browser sends the auth cookie automatically on <img>/<video> tags
// because `withCredentials` is set on the axios client.
const fileUrl = (storagePath) => storagePath
  ? `${API_BASE}/files/view?path=${encodeURIComponent(storagePath)}`
  : null;

// Pick the best video MIME type the browser+backend both accept.
const pickRecorderMime = () => {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    "video/mp4;codecs=avc1.42E01E",  // H.264 baseline (Safari/Chrome ≥ recent)
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  for (const m of candidates) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return null;
};

/**
 * Real-time canvas+MediaRecorder compression pass.
 * - Plays the source video into a hidden <video>.
 * - Draws each frame to an offscreen <canvas> scaled to ≤720p.
 * - Records `canvas.captureStream()` into a smaller VP9/MP4/WebM clip.
 * Returns a Blob (or the original File if compression isn't supported or yields no savings).
 * Reports progress 0–100 via onProgress.
 */
async function compressVideo(file, onProgress) {
  const mime = pickRecorderMime();
  if (!mime || typeof HTMLCanvasElement.prototype.captureStream !== "function") {
    return file; // unsupported — upload original
  }
  const srcUrl = URL.createObjectURL(file);
  try {
    const v = document.createElement("video");
    v.src = srcUrl; v.muted = true; v.playsInline = true; v.preload = "auto";
    await new Promise((res, rej) => {
      v.onloadedmetadata = res; v.onerror = () => rej(new Error("Cannot read video"));
    });
    const ratio = Math.min(1, COMPRESS_MAX_HEIGHT / v.videoHeight);
    const w = Math.round(v.videoWidth * ratio / 2) * 2;
    const h = Math.round(v.videoHeight * ratio / 2) * 2;
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");

    const stream = canvas.captureStream(30);
    const chunks = [];
    const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: COMPRESS_BITRATE });
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };

    const done = new Promise((res) => { recorder.onstop = () => res(); });
    recorder.start(250);

    await v.play();
    const duration = v.duration || MAX_VIDEO_SEC;
    let rafId = null;
    const draw = () => {
      ctx.drawImage(v, 0, 0, w, h);
      if (onProgress && duration) {
        onProgress(Math.min(99, Math.round((v.currentTime / duration) * 100)));
      }
      if (!v.ended && !v.paused) rafId = requestAnimationFrame(draw);
    };
    draw();
    await new Promise((res) => { v.onended = res; });
    if (rafId) cancelAnimationFrame(rafId);
    recorder.stop();
    await done;
    stream.getTracks().forEach(t => t.stop());

    const outBlob = new Blob(chunks, { type: mime.split(";")[0] });
    if (outBlob.size >= file.size * 0.9) return file;  // negligible savings — keep original
    onProgress && onProgress(100);
    // Preserve a sensible filename + extension
    const ext = mime.startsWith("video/mp4") ? "mp4" : "webm";
    const baseName = (file.name || "video").replace(/\.[^.]+$/, "");
    return new File([outBlob], `${baseName}.${ext}`, { type: mime.split(";")[0] });
  } finally {
    URL.revokeObjectURL(srcUrl);
  }
}

export default function SecurityAlerts() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("general");
  const [photos, setPhotos] = useState([]); // [{file_url, filename}]
  const [video, setVideo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);  // 0–100
  const [uploadPhase, setUploadPhase] = useState("");  // "compressing" | "uploading"
  const [submitting, setSubmitting] = useState(false);
  const [commentDrafts, setCommentDrafts] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/security-alerts");
      setPosts(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", "security-alerts");
    const { data } = await api.post("/uploads", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
    return { file_url: data.file_url, filename: data.filename };
  };

  const onAddPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (photos.length >= MAX_PHOTOS) { toast.error(`Max ${MAX_PHOTOS} photos`); return; }
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    setUploading(true);
    setUploadProgress(0);
    try { const up = await uploadFile(file); setPhotos(p => [...p, up]); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); setUploadProgress(0); }
  };

  const onAddVideo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please choose a video"); return; }
    if (file.size > MAX_VIDEO_BYTES) { toast.error("Video too large (max 60MB)"); return; }
    // Client-side duration check
    const ok = await new Promise(resolve => {
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(v.duration <= MAX_VIDEO_SEC); };
      v.onerror = () => { URL.revokeObjectURL(url); resolve(true); }; // be lenient if metadata can't read
      v.src = url;
    });
    if (!ok) { toast.error(`Video must be ${MAX_VIDEO_SEC} seconds or shorter`); return; }
    setUploading(true);
    try {
      // 1. Compress (real-time canvas re-encode → ~60% smaller, falls back on unsupported)
      setUploadPhase("compressing"); setUploadProgress(0);
      const originalSize = file.size;
      const compressed = await compressVideo(file, (pct) => setUploadProgress(pct));
      if (compressed !== file) {
        const saved = Math.round(((originalSize - compressed.size) / originalSize) * 100);
        if (saved > 0) toast.success(`Compressed ${saved}% smaller — uploading…`);
      }
      // 2. Upload
      setUploadPhase("uploading"); setUploadProgress(0);
      const up = await uploadFile(compressed);
      setVideo(up);
    } catch (err) { toast.error(formatApiError(err) || "Video processing failed"); }
    finally { setUploading(false); setUploadProgress(0); setUploadPhase(""); }
  };

  const removePhoto = (i) => setPhotos(p => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) { toast.error("Please describe what you saw"); return; }
    setSubmitting(true);
    try {
      await api.post("/security-alerts", {
        title: title.trim() || null,
        content: content.trim(),
        category,
        photos: photos.map(p => p.file_url),
        video_url: video?.file_url || null,
      });
      setTitle(""); setContent(""); setCategory("general"); setPhotos([]); setVideo(null);
      toast.success("Posted to the security feed");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this post?")) return;
    try { await api.delete(`/security-alerts/${id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const submitComment = async (id) => {
    const msg = (commentDrafts[id] || "").trim();
    if (!msg) return;
    try {
      await api.post(`/security-alerts/${id}/comment`, { message: msg });
      setCommentDrafts(d => ({ ...d, [id]: "" }));
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const canDelete = (post) => {
    if (user?.role === "admin") return true;
    if (post.author_id !== user?.id) return false;
    const created = new Date(post.created_at).getTime();
    return Date.now() - created < 60 * 60 * 1000;
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="fade-up">
        <h1 className="font-serif text-3xl font-bold text-[#1A2E24] flex items-center gap-3">
          <ShieldAlert size={28} className="text-[#C92A2A]"/> Security Feed
        </h1>
        <p className="text-[#5E7166] text-sm">
          Share anything you see — suspicious activity, safety concerns, lost & found. Posts here are visible to the property manager, admin and all active tenants.
        </p>
        <p className="text-xs text-[#7A6810] mt-2 flex items-center gap-1.5 bg-[#FDF6E3] border border-[#C9A227]/30 rounded-md px-3 py-2 inline-flex">
          <AlertTriangle size={14}/> Posts must relate to security, safety or awareness. Off-topic posts may be removed by an admin.
        </p>
      </div>

      {/* New-post composer */}
      <Card title="New post">
        <form onSubmit={submit} className="space-y-3" data-testid="security-post-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input value={title} onChange={e=>setTitle(e.target.value)} className="input-modav md:col-span-2" placeholder="Short title (optional)" data-testid="sec-title"/>
            <select value={category} onChange={e=>setCategory(e.target.value)} className="input-modav" data-testid="sec-category">
              {CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
            </select>
          </div>
          <textarea value={content} onChange={e=>setContent(e.target.value)} rows={3} maxLength={2000}
                    className="input-modav resize-none" placeholder="What did you see? When and where?" data-testid="sec-content"/>
          {/* Attachments */}
          <div className="flex flex-wrap items-center gap-3">
            {photos.map((p, i) => (
              <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#E5EBE8]" data-testid={`sec-photo-${i}`}>
                <img src={fileUrl(p.file_url)} alt="" className="w-full h-full object-cover"/>
                <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"><X size={12}/></button>
              </div>
            ))}
            {photos.length < MAX_PHOTOS && (
              <label className="w-20 h-20 rounded-lg border-2 border-dashed border-[#E5EBE8] flex flex-col items-center justify-center cursor-pointer hover:border-[#C9A227] text-[#5E7166] text-[10px]" data-testid="sec-add-photo">
                <ImageIcon size={18}/><span>Photo</span>
                <input type="file" accept="image/*" onChange={onAddPhoto} className="hidden"/>
              </label>
            )}
            {!video && (
              <label className="px-3 h-20 rounded-lg border-2 border-dashed border-[#E5EBE8] flex flex-col items-center justify-center cursor-pointer hover:border-[#C9A227] text-[#5E7166] text-[10px]" data-testid="sec-add-video">
                <Video size={18}/><span>Video ≤{MAX_VIDEO_SEC}s</span>
                <input type="file" accept="video/*" onChange={onAddVideo} className="hidden"/>
              </label>
            )}
            {video && (
              <div className="relative h-20 rounded-lg overflow-hidden border border-[#E5EBE8] flex items-center gap-2 bg-[#0E5C3A]/5 px-3" data-testid="sec-video-preview">
                <Video size={16} className="text-[#0E5C3A]"/>
                <span className="text-xs truncate max-w-[160px]">{video.filename}</span>
                <button type="button" onClick={() => setVideo(null)} className="text-[#C92A2A]"><X size={14}/></button>
              </div>
            )}
            {uploading && (
              <div className="flex items-center gap-2 text-xs text-[#5E7166] min-w-[220px]" data-testid="upload-progress">
                <span className="font-semibold text-[#1A2E24] uppercase tracking-wider text-[10px]" data-testid="upload-phase">
                  {uploadPhase === "compressing" ? "Compressing" : "Uploading"}
                </span>
                <div className="flex-1 h-1.5 bg-[#E5EBE8] rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-200 ${uploadPhase === "compressing" ? "bg-[#0E5C3A]" : "bg-[#C9A227]"}`}
                    style={{ width: `${uploadProgress}%` }}
                    data-testid="upload-progress-bar"
                  />
                </div>
                <span className="font-mono tabular-nums w-10 text-right" data-testid="upload-progress-pct">{uploadProgress}%</span>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-[#5E7166]">{content.length}/2000 · max {MAX_PHOTOS} photos · 1 short video</span>
            <button type="submit" disabled={submitting || uploading || !content.trim()} className="btn-primary inline-flex items-center gap-2" data-testid="sec-submit">
              <Send size={14}/> {submitting ? "Posting…" : "Post"}
            </button>
          </div>
        </form>
      </Card>

      {/* Feed */}
      {loading ? <Loading/> : posts.length === 0 ? (
        <Card><div className="text-center py-10 text-[#5E7166] text-sm">No posts yet — be the first to share something.</div></Card>
      ) : (
        <div className="space-y-3">
          {posts.map(p => (
            <Card key={p.id} className="!p-0" testid={`sec-post-${p.id}`}>
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#0E5C3A] text-white font-bold flex items-center justify-center">{(p.author_name||"?")[0]?.toUpperCase()}</div>
                    <div>
                      <div className="font-semibold text-sm">{p.author_name} <span className="text-[10px] text-[#5E7166] font-normal ml-1 uppercase tracking-wider">{p.author_role}</span></div>
                      <div className="text-[11px] text-[#5E7166]">{formatDateTime(p.created_at)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${CAT_COLOR[p.category] || CAT_COLOR.general}`}>
                      <TagIcon size={10} className="inline mr-1"/>{(p.category || "general").replace("_", " ")}
                    </span>
                    {canDelete(p) && (
                      <button onClick={() => remove(p.id)} title="Delete post" className="text-[#C92A2A] hover:bg-[#FEF0F0] p-1.5 rounded" data-testid={`sec-delete-${p.id}`}>
                        <Trash2 size={14}/>
                      </button>
                    )}
                  </div>
                </div>
                {p.title && <div className="mt-3 font-semibold text-[#1A2E24]">{p.title}</div>}
                <div className="mt-1 text-sm text-[#1A2E24] whitespace-pre-wrap leading-relaxed">{p.content}</div>
                {p.photos?.length > 0 && (
                  <div className={`mt-3 grid gap-2 ${p.photos.length === 1 ? "grid-cols-1" : "grid-cols-2 md:grid-cols-3"}`}>
                    {p.photos.map((src, i) => (
                      <a key={i} href={fileUrl(src)} target="_blank" rel="noreferrer" className="block aspect-square rounded-lg overflow-hidden border border-[#E5EBE8]">
                        <img src={fileUrl(src)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform"/>
                      </a>
                    ))}
                  </div>
                )}
                {p.video_url && (
                  <video controls preload="metadata" className="mt-3 w-full max-w-md rounded-lg border border-[#E5EBE8] bg-black" data-testid={`sec-video-${p.id}`} crossOrigin="use-credentials">
                    <source src={fileUrl(p.video_url)}/>
                    Your browser does not support inline video. <a href={fileUrl(p.video_url)} target="_blank" rel="noreferrer" className="text-[#C9A227] underline">Open video</a>.
                  </video>
                )}
              </div>
              {/* Comments */}
              {p.comments?.length > 0 && (
                <div className="border-t border-[#E5EBE8] bg-[#F7F9F8]">
                  {p.comments.map(c => (
                    <div key={c.id} className="px-4 py-2 text-sm border-b border-[#E5EBE8] last:border-0">
                      <span className="font-semibold">{c.author_name}</span>
                      <span className="text-[10px] text-[#5E7166] uppercase tracking-wider ml-1">{c.author_role}</span>
                      <span className="text-[#5E7166] mx-2">·</span>
                      <span>{c.message}</span>
                      <span className="text-[11px] text-[#5E7166] ml-2">{formatDateTime(c.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="border-t border-[#E5EBE8] px-4 py-2 flex items-center gap-2">
                <MessageCircle size={14} className="text-[#5E7166]"/>
                <input
                  value={commentDrafts[p.id] || ""}
                  onChange={e => setCommentDrafts(d => ({ ...d, [p.id]: e.target.value }))}
                  onKeyDown={e => { if (e.key === "Enter") submitComment(p.id); }}
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-[#5E7166]"
                  placeholder="Add a comment…"
                  maxLength={500}
                  data-testid={`sec-comment-input-${p.id}`}
                />
                <button onClick={() => submitComment(p.id)} className="text-[#0E5C3A] hover:text-[#C9A227] disabled:opacity-50" disabled={!(commentDrafts[p.id] || "").trim()} data-testid={`sec-comment-send-${p.id}`}>
                  <Send size={14}/>
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
